import React, { createContext, useContext, useEffect, useState } from 'react';
import { generateRecaptchaToken } from '@jbhi-fi/apis/src/utils/recaptcha/generateRecaptchaToken';

export type RecaptchaProps = {
  siteKey: string;
};

export type RecaptchaContext =
  | { isReady?: boolean; generateToken?: (action: string) => Promise<string | null> }
  | { isReady: true; generateToken: (action: string) => Promise<string | null> };

export const RecaptchaContext = createContext<RecaptchaContext | null>(null);

export const RecaptchaProvider = ({ children, siteKey }: RecaptchaProps & { children: React.ReactNode }) => {
  const [isReady, setIsReady] = useState<boolean>(!siteKey || window.grecaptcha?.isReady || false);

  useEffect(() => {
    if (isReady || !siteKey) return;
    let shouldCancel = false;
    if (window.grecaptcha?.isReady) setIsReady(true);

    window.grecaptcha = new Proxy(window.grecaptcha ?? { enterprise: {} }, {
      set(obj, prop, value) {
        const wasPropertySet = Reflect.set(obj, prop, value);
        if (!shouldCancel && wasPropertySet && prop === 'isReady' && isReady !== value) {
          setIsReady(Boolean(value));
        }
        return wasPropertySet;
      },
    });

    return () => {
      shouldCancel = true;
    };
  }, [isReady, siteKey]);

  return (
    <RecaptchaContext.Provider
      value={{
        generateToken: isReady && siteKey ? (action: string) => generateRecaptchaToken(siteKey, action) : undefined,
        isReady,
      }}
    >
      {children}
    </RecaptchaContext.Provider>
  );
};

export const useRecaptchaContext = (): RecaptchaContext => {
  const context = useContext(RecaptchaContext);
  if (context === undefined || context === null) {
    throw new Error('useRecaptchaContext must be used within a RecaptchaProvider');
  }
  return context;
};
